import { Checkbox, Tooltip } from '@mui/material';
import {
  DataGrid,
  DataGridProps,
  GridColDef,
  GridRenderCellParams,
  GridValueFormatterParams,
} from '@thingslog/ui-components';
import { format } from 'date-fns';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DisplayUnit } from '@thingslog/repositories';
import { ReadingsExportDto } from '@thingslog/repositories';

const CountersTable: React.FC<CountersTableProps> = ({
  pageSize,
  autoHeight,
  rowsPerPageOptions,
  onPageSizeChange,
  selectedDeviceNumber,
  showSelection,
  ...props
}: CountersTableProps) => {
  const { t } = useTranslation();
  const [size, setSize] = useState(10);

  const onPageSizeChangeHandler = (pageSize: number): void => setSize(pageSize);

  const renderReadingCell = (
    params: GridRenderCellParams<number | null, ReadingsExportDto>
  ): JSX.Element => {
    let cellContent: string = '';

    const value = params.value;
    const unit = params.row.unit;
    const displayUnit = unit ? DisplayUnit[unit] : null;

    if (typeof value === 'number') {
      if (displayUnit !== null) {
        cellContent = `${value} ${displayUnit}`;
      } else if (displayUnit === null) {
        cellContent = `${value}`;
      }
    }

    return <span>{cellContent}</span>;
  };

  const renderSelectionCell = (
    params: GridRenderCellParams<unknown, ReadingsExportDto>
  ): JSX.Element => {
    const isSelected = params.row.deviceNumber === selectedDeviceNumber;

    return (
      <Tooltip title={t('link_selected_device') || ''} disableHoverListener={!isSelected}>
        <span>
          <Checkbox checked={isSelected} size="small" disableRipple />
        </span>
      </Tooltip>
    );
  };

  const columns: GridColDef[] = [
    {
      field: 'date',
      headerName: t('counters_export_table_date'),
      flex: 1,
      minWidth: 150,
      valueFormatter: (params: GridValueFormatterParams<string>) =>
        params.value ? format(new Date(params.value), 'dd MMM yyyy HH:mm') : '',
    },
    {
      field: 'deviceName',
      headerName: t('counters_export_table_device_name'),
      flex: 1,
      minWidth: 140,
    },
    {
      field: 'deviceNumber',
      headerName: t('counters_export_table_device_number'),
      flex: 1,
      minWidth: 100,
    },
    {
      field: 'sensorIndex',
      headerName: t('counters_export_table_sensor_index'),
      headerAlign: 'left',
      type: 'number',
      flex: 1,
      minWidth: 90,
    },
    {
      field: 'sensorName',
      headerName: t('counters_export_table_sensor_name'),
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'counters',
      headerName: t('counters_export_table_counters'),
      type: 'number',
      headerAlign: 'left',
      flex: 1,
      minWidth: 150,
      renderCell: renderReadingCell,
    },
    {
      field: 'previousCounter',
      headerName: t('counters_export_table_prev_counters'),
      type: 'number',
      headerAlign: 'left',
      flex: 1,
      minWidth: 150,
      renderCell: renderReadingCell,
    },
    {
      field: 'dt',
      type: 'number',
      headerAlign: 'left',
      headerName: t('counters_export_table_dt'),
      flex: 1,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams<number | null, ReadingsExportDto>): JSX.Element => {
        const value = params.value;

        return <span>{typeof value === 'number' && `${value} s`}</span>;
      },
    },
    {
      field: 'delta',
      type: 'number',
      headerAlign: 'left',
      headerName: t('counters_export_table_delta'),
      flex: 1,
      minWidth: 150,
      renderCell: renderReadingCell,
    },
  ];

  const visibleColumns = showSelection
    ? [
        {
          field: 'selection',
          headerName: '',
          width: 60,
          sortable: false,
          filterable: false,
          disableColumnMenu: true,
          align: 'center',
          renderCell: renderSelectionCell,
        } as GridColDef,
        ...columns,
      ]
    : columns;

  return (
    <DataGrid
      pageSize={pageSize ? pageSize : size}
      rowsPerPageOptions={rowsPerPageOptions ? rowsPerPageOptions : [10, 25, 50]}
      autoHeight={autoHeight ? autoHeight : true}
      onPageSizeChange={onPageSizeChange ? onPageSizeChange : onPageSizeChangeHandler}
      columns={visibleColumns}
      {...props}
    />
  );
};

interface CountersTableProps extends Omit<DataGridProps, 'columns'> {
  selectedDeviceNumber: string | null;
  showSelection: boolean;
}

export default CountersTable;
